import { search } from './searchService';
import { getSilent, getById, post, put, del } from './apiService';
import { searchBusinesses, TEAM_SEARCH_EXPAND } from './businessService';
import { searchProfiles } from './profileService';
import { profileToAuditRow, sortAuditRows } from '../utils/clientAuditUtils';
import { getSummaryMember } from '../types/teamSummary';
import type { Client, CreateClientWithPlanPayload, CreateClientWithPlanResponse, ClientTeamPlanResponse } from '../types/client';
import type { ClientAuditTeamSection } from '../types/clientAudit';
import type { SearchParams } from '../types/search';

export async function searchClients(
  filter: Record<string, unknown> = {},
  params: SearchParams = {},
): Promise<Client[]> {
  return search<Client>('/client/search', filter, params);
}

export async function getClientById(id: string): Promise<Client> {
  return getById<Client>('/client', id);
}

export async function createClient(data: { name: string; demo?: boolean }): Promise<Client> {
  return post<Client>('/client', data);
}

export async function createClientWithPlan(data: CreateClientWithPlanPayload): Promise<CreateClientWithPlanResponse> {
  return post<CreateClientWithPlanResponse>('/create-client-with-plan', data);
}

export async function setClientDemo(clientId: string): Promise<Client> {
  return put<Client>(`/client/${clientId}/demo`, {});
}

export async function unsetClientDemo(clientId: string): Promise<Client> {
  return del<Client>(`/client/${clientId}/demo`);
}

export async function updateClient(clientId: string, data: { name: string }): Promise<Client> {
  const client = await getClientById(clientId);
  return put<Client>(`/client/${clientId}`, { ...client, ...data });
}

export async function getClientTeamPlan(clientId: string): Promise<ClientTeamPlanResponse | null> {
  return getSilent<ClientTeamPlanResponse>(`/client/${clientId}/team-plan?includePending=true&includePaused=true&includeInactive=true`);
}

export async function setClientInactive(clientId: string): Promise<void> {
  return put(`/client/${clientId}/inactive`, {});
}

export async function setClientActive(clientId: string): Promise<void> {
  return put(`/client/${clientId}/active`, {});
}

export async function deleteClient(clientId: string): Promise<void> {
  return put(`/client/${clientId}/delete`, {});
}

export async function getClientAuditReport(
  clientId: string,
): Promise<ClientAuditTeamSection[]> {
  const teams = await searchBusinesses(
    { clientId },
    { page: 1, pageSize: 1000, orderBy: 'updated', order: 'desc', expand: TEAM_SEARCH_EXPAND },
  );

  const profileSearchParams = {
    page: 1,
    pageSize: 1000,
    orderBy: 'updated',
    order: 'desc' as const,
    expand: 'business,userPlan,lastAuthentication',
  };

  const sections = await Promise.all(
    teams.map(async (team) => {
      const profiles = await searchProfiles(
        { persona: 'business', businessId: team.id },
        profileSearchParams,
      );

      const rows = profiles.map((profile) => profileToAuditRow(
          profile,
          team.id,
          profile.business?.name || team.name,
          getSummaryMember(team.summary, profile.id),
        ));

      return {
        teamId: team.id,
        teamName: team.name,
        users: sortAuditRows(rows),
      };
    }),
  );

  return sections.sort((a, b) => a.teamName.localeCompare(b.teamName));
}
